import { useCallback, useEffect, useMemo, useState, FC } from "react";
import ModalContainer from "../ModalContainer/ModalContainer";
import { RadioGroup, Stack, Radio, Text, VStack } from "@chakra-ui/react";
import { Group } from "../../types/Group";
import { useNavigate } from "react-router-dom";
import { PROVIDER_PAGES } from "../../base/router/pages";
import { useGroupsWithLnqRights } from "../../hooks/lnq/useGroupsWithLnqRights";
import { WorklistType } from "../../types/Worklist";
import { useGetMyGroups } from "../../api/GroupApi";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (group: Group) => void;
  lnqType?: WorklistType;
}

const GroupsListModal: FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  lnqType = WorklistType.GROUP,
}) => {
  const navigate = useNavigate();
  const [value, setValue] = useState<string | null>();
  const [showNoGroupsMessage, setShowNoGroupsMessage] = useState(false);
  const { groups: groupsWithLnqRights } = useGroupsWithLnqRights();

  const roleFilter = useMemo(
    () => (lnqType === WorklistType.GROUP ? ("admin" as const) : undefined),
    [lnqType],
  );
  const { data: myGroupsData } = useGetMyGroups(1, 0, roleFilter);
  useEffect(() => {
    if (isOpen && (!groupsWithLnqRights || groupsWithLnqRights.length === 0)) {
      setShowNoGroupsMessage(true);
    } else {
      setShowNoGroupsMessage(false);
    }
  }, [isOpen, groupsWithLnqRights]);

  const isSingleGroup = useMemo(
    () => groupsWithLnqRights.length === 1,
    [groupsWithLnqRights],
  );

  const containerTitle = useMemo(() => {
    if (showNoGroupsMessage) {
      return "Join a Group";
    }
    const facilityName =
      import.meta.env.MODE === "production" ? "RABR" : groupsWithLnqRights[0]?.facilityName;
    if (isSingleGroup && !groupsWithLnqRights[0]?.paymentMethodAttached) {
      return "To create your first LnQ, you need to have a valid payment method on file.";
    }

    const lnqTypeLabel = lnqType === WorklistType.PERSONAL ? "personal" : "group";

    return isSingleGroup
      ? `You're activating ${lnqTypeLabel} LnQ for your ${facilityName} worklist. Do you want to proceed?`
      : `You're activating ${lnqTypeLabel} LnQ. Please select a group from the list below.`;
  }, [isSingleGroup, myGroupsData, groupsWithLnqRights, showNoGroupsMessage, lnqType]);
  const [rightButtonTitle, leftButtonTitle] = useMemo(() => {
    if (isSingleGroup && !groupsWithLnqRights[0]?.paymentMethodAttached) {
      return ["Set Up Payment", "Not Now"];
    }

    return isSingleGroup ? ["Yes", "No"] : ["Confirm", "Cancel"];
  }, [isSingleGroup, groupsWithLnqRights]);

  useEffect(() => {
    if (isOpen) {
      setValue(null);
    }
  }, [isOpen]);

  const onClickConfirm = useCallback(() => {
    if (!groupsWithLnqRights) return;
    if (isSingleGroup && !groupsWithLnqRights[0]?.paymentMethodAttached) {
      navigate(`${PROVIDER_PAGES.groups}/edit/${groupsWithLnqRights[0].id}`);
      return;
    }
    if (isSingleGroup) {
      onConfirm(groupsWithLnqRights[0]);
      return;
    }
    if (!value) return;
    const index = groupsWithLnqRights.findIndex((g) => g.id === value);
    if (index !== -1) {
      onConfirm(groupsWithLnqRights[index]);
    }
  }, [isSingleGroup, groupsWithLnqRights, navigate, onConfirm, value]);

  const handleTakeMeToGroups = useCallback(() => {
    onClose();
    navigate("/groups?tab=allgroups");
  }, [navigate, onClose]);

  if (showNoGroupsMessage) {
    return (
      <ModalContainer
        title={containerTitle}
        leftButtonTitle="Not Now"
        rightButtonTitle="Take me to Groups"
        onClickRightButton={handleTakeMeToGroups}
        onClickLeftButton={onClose}
        isOpen={isOpen}
        onClose={onClose}
      >
        <VStack spacing={4} align="stretch">
          <Text color="white">
            {lnqType === WorklistType.GROUP
              ? "To activate your first group LnQ you must be an admin of at least one group."
              : "To activate your first personal LnQ you must join at least one group."}
          </Text>
        </VStack>
      </ModalContainer>
    );
  }

  if (!groupsWithLnqRights || groupsWithLnqRights.length === 0) {
    return null;
  }

  return (
    <ModalContainer
      title={containerTitle}
      leftButtonTitle={leftButtonTitle}
      rightButtonTitle={rightButtonTitle}
      onClickRightButton={onClickConfirm}
      onClickLeftButton={onClose}
      isOpen={isOpen}
      onClose={onClose}
    >
      {!isSingleGroup ? (
        <RadioGroup onChange={setValue} value={value ?? ""} colorScheme="brandYellow">
          <Stack
            justifyItems="flex-start"
            alignItems="left"
            direction="column"
            textColor="white"
            fontWeight="600"
            overflowY="scroll"
            maxH="300px"
            px={2}
          >
            {groupsWithLnqRights.map((group) => {
              return (
                <Radio key={group.id} value={group.id}>
                  {group.facilityName}
                </Radio>
              );
            })}
          </Stack>
        </RadioGroup>
      ) : null}
    </ModalContainer>
  );
};

export default GroupsListModal;
